import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  alpha,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Icon } from '@iconify/react';
import LoadingScreen from 'components/LoadingScreen';
import IdentifierField from 'components/Login/IdentifierField';
import { invitationApi, userApi, InvitationInfo } from 'api';
import { authUtils } from 'utils/auth';
import { useAuthForm } from 'hooks/useAuthForm';
import { PATHS } from 'routes/paths';

type Phase = 'loading' | 'register' | 'login' | 'accepting' | 'error';

/** Достаёт человекочитаемое сообщение из ошибки axios/Nest */
function extractError(e: any): string {
  const m = e?.response?.data?.message;
  if (Array.isArray(m)) return m.join(', ');
  if (typeof m === 'string') return m;
  return '';
}

/**
 * Страница принятия приглашения по ссылке вида /invite?token=...
 *
 * Токен привязан к конкретному кандидату вакансии (а не к email), поэтому
 * email на экране показывается заблокированным — менять его нельзя.
 *
 * Сценарии:
 *  - пользователь уже авторизован  → приглашение привязывается к его аккаунту автоматически;
 *  - у email ещё нет аккаунта       → форма «задать пароль» (регистрация + вход);
 *  - у email уже есть аккаунт       → форма «войти и принять».
 */
export default function InvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Форма (идентификатор, пароли, ошибки) и хелперы — общие с экраном логина/регистрации
  const {
    form,
    ui,
    otpStrategy,
    submitError,
    fieldErrors,
    fieldLabel,
    fieldPlaceholder,
    fieldAutoComplete,
    updateForm,
    updateUi,
    setSubmitError,
    validatePassword,
    handleIdentifierChange,
    handleIdentifierBlur,
    loginAndRedirect
  } = useAuthForm();

  const [phase, setPhase] = useState<Phase>('loading');
  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handledRef = useRef(false);

  /** Поднимает сессию по выданным токенам — так же, как обычный логин/OAuth */
  const enterWithTokens = async (accessToken: string, refreshToken: string) => {
    const userId = authUtils.getUserIdFromAccessToken(accessToken);
    if (!userId) {
      setPhase('error');
      setErrorMsg('Не удалось завершить вход');
      return;
    }
    await loginAndRedirect(accessToken, refreshToken, userId, { replace: true });
  };

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    if (!token) {
      setPhase('error');
      setErrorMsg('Ссылка-приглашение недействительна');
      return;
    }

    (async () => {
      let data: InvitationInfo;
      try {
        data = await invitationApi.getInvitation(token);
      } catch (e: any) {
        setPhase('error');
        setErrorMsg(extractError(e) || 'Приглашение не найдено или срок его действия истёк');
        return;
      }

      setInfo(data);

      // Уже авторизован — принимаем автоматически и привязываем к текущему аккаунту
      if (authUtils.isAuthenticated()) {
        setPhase('accepting');
        try {
          await invitationApi.acceptInvitation(token, {});
          navigate(PATHS.main, { replace: true });
        } catch (e: any) {
          // Токен авторизации протух/чужой — откатываемся к ручному входу
          setPhase(data.requiresPassword ? 'register' : 'login');
          setErrorMsg(extractError(e));
        }
        return;
      }

      setPhase(data.requiresPassword ? 'register' : 'login');
    })();
  }, [token, navigate]);

  // Приглашение приходит на email — подставляем его в поле при email-стратегии.
  // При телефонной стратегии оставляем поле пустым: пользователь вводит свой номер.
  useEffect(() => {
    if (info && otpStrategy === 'email' && !form.identifier) {
      updateForm({ identifier: info.email });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info, otpStrategy]);

  // Регистрация: задаём пароль → бэк создаёт аккаунт и возвращает токены
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const pwdErr = validatePassword(form.regPassword);
    if (pwdErr) {
      setSubmitError(pwdErr);
      return;
    }
    if (form.regPassword !== form.regPasswordConfirm) {
      setSubmitError('Пароли не совпадают');
      return;
    }
    setSubmitting(true);
    try {
      const res = await invitationApi.acceptInvitation(token, {
        password: form.regPassword,
        firstName: firstName || undefined,
        lastName: lastName || undefined
      });
      if (res.status === 'registered' && res.accessToken && res.refreshToken) {
        await enterWithTokens(res.accessToken, res.refreshToken);
      } else {
        navigate(PATHS.main, { replace: true });
      }
    } catch (e: any) {
      // 409 — на этот email уже есть аккаунт: предложим войти
      if (e?.response?.status === 409) {
        setPhase('login');
        setSubmitError(null);
      } else {
        setSubmitError(extractError(e) || 'Не удалось принять приглашение');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Вход существующим аккаунтом → сразу принимаем приглашение под этой сессией
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!info) return;
    setSubmitError(null);
    if (!form.identifier || !form.password) {
      setSubmitError('Введите данные для входа');
      return;
    }
    setSubmitting(true);
    try {
      // Телефон нормализуется/форматируется в IdentifierField и userApi.login,
      // идентификатор уходит на бэк в зависимости от выбранной стратегии входа
      const { accessToken, refreshToken, user } = await userApi.login(
        otpStrategy === 'email' ? form.identifier : undefined,
        otpStrategy === 'phone' ? form.identifier : undefined,
        form.password
      );
      // Сохраняем токены до accept — интерцептор подставит их в заголовок запроса
      authUtils.setTokens(accessToken, refreshToken);
      authUtils.setUserId(user.id as string);
      await invitationApi.acceptInvitation(token, {});
      await enterWithTokens(accessToken, refreshToken);
    } catch (e: any) {
      if (e?.response?.status === 409) {
        setSubmitError('Не удалось войти. Проверьте логин или пароль.');
      } else {
        setSubmitError(extractError(e) || 'Не удалось войти. Проверьте пароль.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderShell = (children: React.ReactNode) => (
    <Stack
      sx={{
        height: '100%',
        position: 'relative',
        backgroundImage: 'url(/static/brand/patternVert.svg)',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: isMobile ? '150%' : 'cover',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundColor: alpha(theme.palette.background.default, 0.92),
          zIndex: 0
        }
      }}
      justifyContent="center"
      alignItems="center"
      spacing={2}
    >
      <Card
        sx={{
          width: { xs: '100%', sm: '50%' },
          maxWidth: 520,
          boxShadow: 6,
          zIndex: 1
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 5 }, '&:last-child': { pb: { xs: 3, sm: 5 } } }}>
          {children}
        </CardContent>
      </Card>
    </Stack>
  );

  if (phase === 'loading') {
    return <LoadingScreen label="Загружаем приглашение…" />;
  }

  if (phase === 'accepting') {
    return <LoadingScreen label="Принимаем приглашение…" />;
  }

  if (phase === 'error') {
    return renderShell(
      <Stack spacing={3} alignItems="center" textAlign="center">
        <Icon icon="mdi:link-variant-off" width={48} height={48} color={theme.palette.error.main} />
        <Typography variant="h5">Приглашение недоступно</Typography>
        <Typography variant="body2" color="text.secondary">
          {errorMsg || 'Срок действия ссылки истёк или она уже была использована.'}
        </Typography>
        <Button variant="contained" onClick={() => navigate(PATHS.root, { replace: true })}>
          На главную
        </Button>
      </Stack>
    );
  }

  // phase: register | login
  const invitationHeader = info && (
    <Stack spacing={1} sx={{ mb: 1 }}>
      <Typography variant="h5">Приглашение на интервью</Typography>
      <Typography variant="body2" color="text.secondary">
        {info.company ? `${info.company} · ` : ''}
        {info.position}
      </Typography>
    </Stack>
  );

  // Вход: редактируемое поле email/телефон (с форматированием) — уходит на бэк при логине
  const identifierField = info && (
    <IdentifierField
      otpStrategy={otpStrategy}
      label={fieldLabel}
      value={form.identifier}
      onChange={handleIdentifierChange}
      onBlur={handleIdentifierBlur}
      error={fieldErrors.identifier}
      placeholder={fieldPlaceholder}
      autoComplete={fieldAutoComplete}
    />
  );

  // Регистрация: email задан токеном приглашения — показываем его заблокированным
  const invitedEmailField = info && (
    <IdentifierField
      otpStrategy="email"
      label="Email"
      value={info.email}
      onChange={() => {
        console.log('onChange')
      }}
      onBlur={() => {
        console.log('onChange')
      }}
      disabled
    />
  );

  const passwordVisibilityAdornment = (visible: boolean, toggle: () => void) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton type="button" edge="end" onClick={toggle}>
          <Icon icon={visible ? 'mdi:eye-off' : 'mdi:eye'} />
        </IconButton>
      </InputAdornment>
    )
  });

  if (phase === 'login') {
    return renderShell(
      <Stack component="form" onSubmit={handleLogin} spacing={2}>
        {invitationHeader}
        <Typography variant="body2" color="text.secondary">
          Этот {fieldLabel.toLowerCase()} уже зарегистрирован в системе
        </Typography>
        {identifierField}
        <TextField
          fullWidth
          label="Пароль"
          type={ui.showPassword ? 'text' : 'password'}
          value={form.password}
          onChange={(e) => {
            updateForm({ password: e.target.value });
            setSubmitError(null);
          }}
          autoComplete="current-password"
          autoFocus
          InputProps={passwordVisibilityAdornment(ui.showPassword, () =>
            updateUi({ showPassword: !ui.showPassword })
          )}
        />
        {submitError && <Alert severity="error">{submitError}</Alert>}
        <Button
          fullWidth
          variant="contained"
          size="large"
          type="submit"
          disabled={!form.identifier || !form.password || submitting}
        >
          {submitting ? 'Входим…' : 'Войти и принять'}
        </Button>
      </Stack>
    );
  }

  // phase === 'register'
  return renderShell(
    <Stack component="form" onSubmit={handleRegister} spacing={2}>
      {invitationHeader}
      <Typography variant="body2" color="text.secondary">
        Придумайте пароль, чтобы создать аккаунт и принять приглашение.
      </Typography>
      {invitedEmailField}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          fullWidth
          label="Имя"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <TextField
          fullWidth
          label="Фамилия"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
      </Stack>
      <TextField
        fullWidth
        label="Пароль"
        type={ui.showRegPassword ? 'text' : 'password'}
        value={form.regPassword}
        onChange={(e) => {
          updateForm({ regPassword: e.target.value });
          setSubmitError(null);
        }}
        autoComplete="new-password"
        InputProps={passwordVisibilityAdornment(ui.showRegPassword, () =>
          updateUi({ showRegPassword: !ui.showRegPassword })
        )}
      />
      <TextField
        fullWidth
        label="Повторите пароль"
        type={ui.showRegPassword ? 'text' : 'password'}
        value={form.regPasswordConfirm}
        onChange={(e) => {
          updateForm({ regPasswordConfirm: e.target.value });
          setSubmitError(null);
        }}
        autoComplete="new-password"
      />
      {submitError && <Alert severity="error">{submitError}</Alert>}
      <Button
        fullWidth
        variant="contained"
        size="large"
        type="submit"
        disabled={!form.regPassword || !form.regPasswordConfirm || submitting}
      >
        {submitting ? 'Создаём аккаунт…' : 'Принять приглашение'}
      </Button>
    </Stack>
  );
}
